/**
 * ExploreContent — web port of `apps/WunderGuide/components/explore/ExploreOverlay.tsx`.
 *
 * Discover-mode-first: search experiences via @wg/api/catalog (same client as
 * RN). Renders backend-driven category chips (useDiscoverCategories), debounced
 * search, and a card grid. Tapping a card initiates a booking via catalogApi.initiateBooking
 * and opens the provider booking flow in an iframe sheet (mirrors RN's
 * AgentWebView).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import catalogApi from '../../api/catalogApi';
import type { CatalogItem, CatalogItemSummary, CatalogCategory } from '@wg/types';
import { catalogItemsToMapPoints } from '@wg/hooks/maps';
import CatalogCard, { type CatalogLayout } from '../catalog/CatalogCard';
import Spinner from '../../common/Spinner';
import Icon from '../../common/Icon';
import NearbyMap, { type NearbyMapMarker } from '../explore/NearbyMap';
import { useLocation } from '../../context/LocationContext';
import { useDiscoverCategories } from '../../hooks/useDiscoverCategories';
import { resolveDiscoverIcon, categoryLabel } from '../../utils/discoverCategories';
import { trackDiscoverCategoryTap } from '../../api/eventsApi';
import './ExploreContent.css';

type ExploreMode = 'nearby' | 'discover';
type ViewMode = 'mix' | 'compact' | 'gallery';

const VIEW_MODES: { key: ViewMode; label: string; icon: React.FC }[] = [
  { key: 'mix', label: 'Mix', icon: GridIcon },
  { key: 'compact', label: 'List', icon: ListIcon },
  { key: 'gallery', label: 'Gallery', icon: ImagesIcon },
];

const viewModeToLayout = (vm: ViewMode): CatalogLayout | undefined => {
  if (vm === 'compact') return 'compact';
  if (vm === 'gallery') return 'hero';
  return undefined; // 'mix' uses item.display.layout
};

// Discover/Book category chips are backend-driven (useDiscoverCategories);
// see ../../utils/discoverCategories for the icon-token map + fallback.

// RN ExploreOverlay search debounce is 1500ms; matched here for parity.
const SEARCH_DEBOUNCE_MS = 1500;
const MIN_SEARCH_CHARS = 3;

interface ExploreContentProps {
  /**
   * Lock the mode toggle to a single mode. RN's BookOverlay wraps
   * ExploreOverlay with `lockedMode="discover"` so the Book tab is a pure
   * catalog/search surface (`apps/WunderGuide/components/book/BookOverlay.tsx`).
   */
  lockedMode?: ExploreMode;
}

const ExploreContent: React.FC<ExploreContentProps> = ({ lockedMode }) => {
  // Explore tab opens on the map / nearby view; Book tab passes
  // lockedMode="discover" to land directly on the catalog search.
  const [mode, setMode] = useState<ExploreMode>(lockedMode ?? 'nearby');
  const { location, hasPermission, requestPermission } = useLocation();
  const [viewMode, setViewMode] = useState<ViewMode>('mix');
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [results, setResults] = useState<CatalogItemSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<CatalogItem | null>(null);
  const [detailSummary, setDetailSummary] = useState<CatalogItemSummary | null>(
    null,
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [bookingPending, setBookingPending] = useState(false);

  // Backend-driven category chips (React Query, 24h staleTime, bundled fallback).
  const { categories } = useDiscoverCategories();

  const handleCategorySelect = useCallback(
    (cat: CatalogCategory, index: number) => {
      const key = cat.key || cat.id;
      trackDiscoverCategoryTap({
        key,
        label: categoryLabel(cat),
        position: index,
      });
      setSelectedCategory(prev => (prev === key ? null : key));
    },
    [],
  );

  const search = useCallback(async () => {
    const trimmed = query.trim();
    const hasValidQuery = trimmed.length >= MIN_SEARCH_CHARS;
    if (!hasValidQuery && !selectedCategory) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.searchCatalog({
        query: hasValidQuery ? trimmed : undefined,
        categories: selectedCategory ? [selectedCategory] : undefined,
        limit: 20,
      });
      setResults(res.results ?? []);
    } catch (err: any) {
      setError('Search failed.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, selectedCategory]);

  useEffect(() => {
    if (mode !== 'discover') return;
    const t = window.setTimeout(search, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [mode, search]);

  const handleCardPress = useCallback(async (item: CatalogItemSummary) => {
    setDetailSummary(item);
    setDetailItem(null);
    setDetailLoading(true);
    try {
      const full = await catalogApi.getCatalogItem(item.id);
      setDetailItem(full);
    } catch {
      setDetailItem(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailItem(null);
    setDetailSummary(null);
    setDetailLoading(false);
  }, []);

  // Map pins are derived from the current search results — the only catalog
  // data that carries coordinates (a coords-only "nearby" query returns
  // nothing server-side). The shared converter keeps RN/web pin derivation
  // identical; tapping a pin opens the detail sheet. When there's no active
  // search the map just centers on the user with no pins (no regression).
  const mapMarkers = useMemo<NearbyMapMarker[]>(() => {
    const byId = new Map(results.map(r => [r.id, r]));
    return catalogItemsToMapPoints(results).map(p => ({
      id: p.id,
      lat: p.lat,
      lng: p.lng,
      onClick: () => {
        const item = byId.get(p.id);
        if (item) handleCardPress(item);
      },
    }));
  }, [results, handleCardPress]);

  const startBooking = useCallback(async () => {
    const item = detailItem ?? detailSummary;
    if (!item) return;
    setBookingPending(true);
    try {
      const init = await catalogApi.initiateBooking(item.id, {
        participants: 1,
      });
      const url =
        (init as any).booking_url ||
        (init as any).url ||
        (init as any).redirect_url;
      if (url) {
        // Open in a new tab. Travel booking partners universally set
        // X-Frame-Options: DENY, so an iframe sheet would 404/blank.
        // window.open also respects user gesture for popup blockers.
        window.open(url, '_blank', 'noopener,noreferrer');
        closeDetail();
      } else {
        setError('Booking flow unavailable.');
      }
    } catch {
      setError('Booking flow unavailable.');
    } finally {
      setBookingPending(false);
    }
  }, [detailItem, detailSummary, closeDetail]);

  return (
    <div className="wg-explore">
      {/* Mode pills — hidden when locked (Book tab uses 'discover' only).
          Labels mirror RN's EXPLORE_MODE_OPTIONS: Nearby + Destination. */}
      {!lockedMode && (
        <div className="wg-explore__topbar">
          <div className="wg-explore__tabs">
            <button
              type="button"
              className={`wg-explore__tab${mode === 'nearby' ? ' is-active' : ''}`}
              onClick={() => setMode('nearby')}
            >
              <PaperPlaneIcon /> <span>Nearby</span>
            </button>
            <button
              type="button"
              className={`wg-explore__tab${mode === 'discover' ? ' is-active' : ''}`}
              onClick={() => setMode('discover')}
            >
              <PinIcon /> <span>Destination</span>
            </button>
          </div>
        </div>
      )}

      {mode === 'discover' && (
        <>
          <div className="wg-explore__search">
            <SearchIcon />
            <input
              type="search"
              placeholder="Search restaurants, tours, attractions…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoCorrect="off"
            />
            {query && (
              <button
                type="button"
                className="wg-explore__search-clear"
                aria-label="Clear search"
                onClick={() => setQuery('')}
              >
                <CloseIcon />
              </button>
            )}
          </div>

          <div className="wg-explore__chips">
            {categories.map((cat, index) => {
              const key = cat.key || cat.id;
              return (
                <button
                  key={key}
                  type="button"
                  className={`wg-explore__chip${selectedCategory === key ? ' is-active' : ''}`}
                  onClick={() => handleCategorySelect(cat, index)}
                >
                  <Icon name={resolveDiscoverIcon(cat.icon)} size={18} />
                  <span>{categoryLabel(cat)}</span>
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="wg-explore__center">
              <Spinner size="medium" />
            </div>
          )}

          {!loading && error && (
            <div className="wg-explore__center">{error}</div>
          )}

          {!loading && !error && results.length === 0 && (
            <div className="wg-explore__center">
              {query || selectedCategory
                ? 'No results — try a different search.'
                : 'Search or pick a category to discover experiences.'}
            </div>
          )}

          {!loading && results.length > 0 && mapMarkers.length > 0 && (
            <div className="wg-explore__results-map" aria-label="Results map">
              <NearbyMap
                center={
                  location
                    ? { lat: location.latitude, lng: location.longitude }
                    : null
                }
                markers={mapMarkers}
                fitToMarkers
              />
            </div>
          )}

          {!loading && results.length > 0 && (
            <>
              <div className="wg-explore__results-head">
                <span className="wg-explore__results-count">
                  {results.length} result{results.length === 1 ? '' : 's'}
                </span>
                <span className="wg-explore__view-modes">
                  {VIEW_MODES.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      className={`wg-explore__view-mode${viewMode === key ? ' is-active' : ''}`}
                      onClick={() => setViewMode(key)}
                      aria-label={label}
                    >
                      <Icon />
                    </button>
                  ))}
                </span>
              </div>
              <div className={`wg-explore__list wg-explore__list--${viewMode}`}>
                {results.map(item => (
                  <CatalogCard
                    key={item.id}
                    item={item}
                    forceLayout={viewModeToLayout(viewMode)}
                    onPress={() => handleCardPress(item)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {mode === 'nearby' && (
        <div className="wg-explore__map" aria-label="Map">
          <NearbyMap
            center={
              location
                ? { lat: location.latitude, lng: location.longitude }
                : null
            }
            markers={mapMarkers}
          />
          {hasPermission === false && (
            <div className="wg-explore__nearby-permission">
              <button
                type="button"
                className="wg-explore__nearby-cta"
                onClick={() => requestPermission()}
              >
                Enable location
              </button>
              <p>
                Showing Honolulu by default. Allow location to center on you.
              </p>
            </div>
          )}
        </div>
      )}

      {(detailSummary || detailItem) && (
        <ExperienceDetailSheet
          summary={detailSummary}
          item={detailItem}
          loading={detailLoading}
          bookingPending={bookingPending}
          onClose={closeDetail}
          onBook={startBooking}
        />
      )}
    </div>
  );
};

/* ── ExperienceDetailSheet ───────────────────────────────────────────── */

const ExperienceDetailSheet: React.FC<{
  summary: CatalogItemSummary | null;
  item: CatalogItem | null;
  loading: boolean;
  bookingPending: boolean;
  onClose: () => void;
  onBook: () => void;
}> = ({ summary, item, loading, bookingPending, onClose, onBook }) => {
  const title = item?.title ?? summary?.title ?? '';
  const description =
    item?.description || item?.short_description || summary?.short_description;
  const heroUrl =
    (item?.image && (item.image as any).url) ||
    (summary?.image && (summary.image as any).url) ||
    null;
  const priceObj: any = item?.price ?? summary?.price;
  const price = priceObj
    ? (priceObj.from_amount ?? priceObj.amount ?? null)
    : null;
  const currency = priceObj?.currency || 'USD';
  const ratingObj: any =
    (item as any)?.rating ?? (summary as any)?.reviews ?? null;
  const rating = ratingObj
    ? (ratingObj.average ?? ratingObj.rating ?? null)
    : null;
  const reviews = ratingObj?.count ?? null;
  const provider = item?.provider_name ?? summary?.provider_name;
  const bookable =
    (item?.booking_available ?? summary?.booking_available) === true;
  const highlights = item?.highlights ?? [];
  const inclusions = item?.inclusions ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="wg-detail" role="dialog" aria-modal="true">
      <button
        type="button"
        className="wg-detail__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="wg-detail__sheet">
        <button
          type="button"
          className="wg-detail__close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        {heroUrl && (
          <div
            className="wg-detail__hero"
            style={{ backgroundImage: `url(${heroUrl})` }}
          />
        )}

        <div className="wg-detail__body">
          <h3 className="wg-detail__title">{title || 'Experience'}</h3>

          <div className="wg-detail__meta">
            {rating != null && (
              <span className="wg-detail__rating">
                ★ {Number(rating).toFixed(1)}
                {reviews ? ` (${reviews})` : ''}
              </span>
            )}
            {provider && (
              <span className="wg-detail__provider">via {provider}</span>
            )}
            {price != null && (
              <span className="wg-detail__price">
                from {currency} {Number(price).toFixed(0)}
              </span>
            )}
          </div>

          {loading && (
            <div className="wg-detail__center">
              <Spinner size="medium" />
            </div>
          )}

          {!loading && description && (
            <p className="wg-detail__desc">{description}</p>
          )}

          {!loading && highlights.length > 0 && (
            <div className="wg-detail__section">
              <h4>Highlights</h4>
              <ul>
                {highlights.slice(0, 6).map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </div>
          )}

          {!loading && inclusions.length > 0 && (
            <div className="wg-detail__section">
              <h4>What's included</h4>
              <ul>
                {inclusions.slice(0, 6).map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="wg-detail__footer">
          {bookable ? (
            <button
              type="button"
              className="wg-detail__cta"
              onClick={onBook}
              disabled={bookingPending}
            >
              {bookingPending ? 'Opening…' : 'Book now'}
            </button>
          ) : (
            <span className="wg-detail__unavail">
              Booking not available right now
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreContent;

/* ── Map placeholder + glyphs ────────────────────────────────────────── */

function MapPlaceholder() {
  return (
    <svg
      viewBox="0 0 414 380"
      preserveAspectRatio="xMidYMid slice"
      width="100%"
      height="100%"
      aria-hidden
    >
      <defs>
        <linearGradient id="wgMapBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#26334a" />
          <stop offset="100%" stopColor="#1c2740" />
        </linearGradient>
      </defs>
      <rect width="414" height="380" fill="url(#wgMapBg)" />
      <path
        d="M0 0 L120 0 L160 60 L80 110 L0 90 Z"
        fill="#2c4f3e"
        opacity="0.7"
      />
      <path
        d="M260 0 L414 0 L414 130 L320 110 L260 50 Z"
        fill="#2c4f3e"
        opacity="0.65"
      />
      <path
        d="M0 240 L90 220 L130 290 L60 360 L0 360 Z"
        fill="#2c4f3e"
        opacity="0.7"
      />
      <path
        d="M310 240 L414 230 L414 380 L290 380 L310 240 Z"
        fill="#2c4f3e"
        opacity="0.6"
      />
      <path
        d="M-10 110 Q150 100 414 130"
        stroke="#3d5079"
        strokeWidth="6"
        fill="none"
      />
      <path
        d="M-10 200 Q160 220 414 200"
        stroke="#3d5079"
        strokeWidth="6"
        fill="none"
      />
      <path d="M120 0 L130 380" stroke="#3d5079" strokeWidth="3" fill="none" />
      <path d="M210 0 L210 380" stroke="#3d5079" strokeWidth="3" fill="none" />
      <path d="M300 0 L290 380" stroke="#3d5079" strokeWidth="3" fill="none" />
    </svg>
  );
}

function PaperPlaneIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 22s7-7.58 7-13a7 7 0 1 0-14 0c0 5.42 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.6" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.5" y2="16.5" />
    </svg>
  );
}
function GridIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}
function ListIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="3.5" cy="6" r="1.4" fill="currentColor" />
      <circle cx="3.5" cy="12" r="1.4" fill="currentColor" />
      <circle cx="3.5" cy="18" r="1.4" fill="currentColor" />
    </svg>
  );
}
function ImagesIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="14" height="14" rx="2" />
      <path d="M21 7v14H7" />
    </svg>
  );
}
// Category chip icons are now rendered via common/Icon (Ionicons) from the
// backend-driven `icon` token — the inline Fork/Camera/Sparkle/Walk/Moon/Leaf
// SVG components were removed.
function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
